import { z } from "zod";
import { storableText } from "./appointments";
import { membershipRole, recordId } from "./contracts";
import { emailAddress, SETTINGS_NAME_MAX_LENGTH, SETTINGS_SHORT_TEXT_MAX_LENGTH } from "./organization-settings";

export const INVITED_PASSWORD_MIN_LENGTH = 8;

export const INVITED_PASSWORD_MAX_LENGTH = 72;

export const inviteKind = z.enum(["team", "worker", "subcontractor", "vendor", "customer", "vendorRelationship"]);
export type InviteKind = z.infer<typeof inviteKind>;

export const invite = z.object({
  kind: inviteKind,
  inviterName: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  status: z.literal("sent"),
});
export type Invite = z.infer<typeof invite>;

export const invitedLoginRequest = z
  .object({
    email: emailAddress,
    password: z.string().min(INVITED_PASSWORD_MIN_LENGTH).max(INVITED_PASSWORD_MAX_LENGTH),
    name: storableText(SETTINGS_NAME_MAX_LENGTH),
  })
  .strict();
export type InvitedLoginRequest = z.input<typeof invitedLoginRequest>;

export const invitedLogin = z.object({ canSignIn: z.literal(true) });
export type InvitedLogin = z.infer<typeof invitedLogin>;

export const inviteAcceptRequest = z
  .object({
    name: storableText(SETTINGS_NAME_MAX_LENGTH).optional(),
    phone: z.string().trim().max(SETTINGS_SHORT_TEXT_MAX_LENGTH).optional(),
    organizationName: storableText(SETTINGS_NAME_MAX_LENGTH).optional(),
  })
  .strict();
export type InviteAcceptRequest = z.input<typeof inviteAcceptRequest>;
export type InviteAcceptance = z.output<typeof inviteAcceptRequest>;

export const inviteAccepted = z.object({ person: recordId, organization: recordId });
export type InviteAccepted = z.infer<typeof inviteAccepted>;

export const INVITED_SERVICES_MAX = 200;

const invitedServices = z.array(recordId.toLowerCase()).max(INVITED_SERVICES_MAX).default([]);

export const inviteSendRequest = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("team"), email: emailAddress, name: storableText(SETTINGS_NAME_MAX_LENGTH), role: membershipRole }).strict(),
  z.object({ kind: z.literal("worker"), email: emailAddress, services: invitedServices }).strict(),
  z.object({ kind: z.literal("subcontractor"), email: emailAddress, services: invitedServices }).strict(),
  z.object({ kind: z.literal("vendor"), email: emailAddress }).strict(),
  z.object({ kind: z.literal("customer"), email: emailAddress }).strict(),
]);
export type InviteSendRequest = z.input<typeof inviteSendRequest>;
export type InviteToSend = z.output<typeof inviteSendRequest>;

export const inviteOutcome = z.enum(["invited", "linked"]);
export type InviteOutcome = z.infer<typeof inviteOutcome>;

export const inviteSent = z.object({ outcome: inviteOutcome, kind: inviteKind, email: z.string() });
export type InviteSent = z.infer<typeof inviteSent>;

export const vendorRelationshipAnswer = z.object({ status: z.enum(["accepted", "declined"]) });
export type VendorRelationshipAnswer = z.infer<typeof vendorRelationshipAnswer>;
